import { Component, OnInit, AfterViewInit, ElementRef, NgZone, ViewChild } from '@angular/core';
import { OtcService } from '../../../services/otc.service';
import { RootScopeService } from 'src/app/services/root-scope.service';
import { LogService } from 'src/app/services/log.service';
import * as Constants from '../../../models/Constants';

@Component({
  selector: 'app-otc-phone-flow',
  templateUrl: './otc-phone-flow.component.html',
  styleUrls: ['./otc-phone-flow.component.scss']
})
export class OtcPhoneFlowComponent implements OnInit, AfterViewInit {

  @ViewChild('telInput', { static: false, read: ElementRef }) telInputRef!: ElementRef;
  countrySelected = true;
  fileName: string = "one-time-code_component";
  countryFlag: string = 'IN'; 
  countryMutationObserver: MutationObserver | null = null;
  preSelectedCountryCode: boolean = false;

  constructor(
    public otcService: OtcService,
    public ngZone: NgZone,
    public rootScope: RootScopeService,
    public logger: LogService
  ) { }

  ngOnInit(): void {
    this.setCountryFlag();
  }

  ngAfterViewInit(): void {
    this.initializeCountryInputObservers();
  }

  /**
   * Sets the country flag based on the selected phone number's country code.
   * If the country code is 'IN' or not found, it defaults to 'IN' and sets 
   * `countrySelected` to false. Otherwise, it updates the `countryFlag` 
   */
  setCountryFlag() {
    let countryCode = this.rootScope.activeCustTab.otcData.selectedCountryCode || Constants.COUNTRY_CODES.International;

    if (countryCode === Constants.COUNTRY_CODES.International || countryCode === '') {
      this.countrySelected = false;
      this.countryFlag = Constants.COUNTRY_CODES.International; // Default to International if no country code is found
    } else {
      if(countryCode == Constants.COUNTRY_CODES.United_Kingdom){
        countryCode = Constants.COUNTRY_CODES.Great_Britain;
      }
      this.countryFlag = countryCode;
      this.preSelectedCountryCode = true;
    }
  }

  
  closeFilter() {
    this.otcService.openCountryCodeDialog = false;
  }

  /**
   * Continues the flow to step 3 of the OTC process.
   * This method updates the state to indicate that step 3 is active
   * and logs the action with the current customer session ID.
   */
  continueOnStep2() {
    let countryCode = this.rootScope.activeCustTab.otcData.selectedCountryCode;

    if(countryCode == Constants.COUNTRY_CODES.Great_Britain){
      this.rootScope.activeCustTab.otcData.selectedCountryCode = Constants.COUNTRY_CODES.United_Kingdom;
    }

    this.otcService.openCountryCodeDialog = false;
    this.rootScope.activeCustTab.otcData.step3Active = true;
    this.logger.infoLogs('OTC Step 3 Active for customerSessionId:' + this.rootScope.activeCustTab?.customerSessionId, this.fileName);
  }

  /**
   * Updates the selected country code in the OTC data based on the event details.
   * This method is triggered when a country selection event occurs and updates
   */
  updateCountryCodeForPhone(newCountryCode: any) {
    this.rootScope.activeCustTab.otcData.selectedCountryCode = newCountryCode;
  }

 
  /**
   * Initializes observers for the country input element.
   * This method runs outside of Angular's zone to avoid triggering change detection unnecessarily.
   * It checks for the presence of a native element reference for the telephone input.
   * If the shadow root of the telephone input is available, it sets up a MutationObserver
   * to monitor changes in the child elements of the shadow root. When mutations are detected,
   * it calls the `disableAndObserveCountryInput` method to handle the changes accordingly.
   */
  initializeCountryInputObservers() {
    this.ngZone.runOutsideAngular(() => {
      const telInputNative: HTMLElement | null = this.telInputRef?.nativeElement;
      if (!telInputNative) return;

      const shadowRoot = (telInputNative as any)?.shadowRoot;
       /* istanbul ignore next */
      if (shadowRoot) {
        this.countryMutationObserver = new MutationObserver(() => {
          this.disableAndObserveCountryInput();
        });
        this.countryMutationObserver.observe(shadowRoot, { childList: true, subtree: true });
      }
    });
  }

  /**
   * Disables the country input field and sets up a MutationObserver to monitor changes
   * to the country selection button's attributes. When the attributes 'title' or 
   * 'aria-label' change, it retrieves the selected country from the dropdown menu 
   * and updates the input field with the active customer's phone number. Additionally, 
   * it updates the country code based on the selected country value and manages the 
   * visibility of the country flag.
   */
 /* istanbul ignore next */
    disableAndObserveCountryInput = () => {
    const telInputNative: HTMLElement | null = this.telInputRef?.nativeElement;    
    const shadowRoot = (telInputNative as any)?.shadowRoot;
    const countryButton = shadowRoot.querySelector('button[role="combobox"]');
 
    if (shadowRoot) {
      const input = shadowRoot.querySelector('input');
      input.value = this.rootScope.activeCustTab?.otcData?.selectedPhoneNumber || '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      if (input && !input.disabled) {
        input.disabled = true;
      }
 
 
       if (countryButton && !(countryButton as any)._countryMutationObserverAdded) {
 
        let countryListObserver = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation?.type === 'attributes' && (mutation?.attributeName === 'title' || mutation?.attributeName === 'aria-label')) {
              const menu = shadowRoot?.querySelector('[role="listbox"]');
              let countryValue = '';
 
              if (menu) {
                const selectedOption = menu?.querySelector('button[aria-selected="true"]') ||
                  Array.from(menu?.querySelectorAll('button[role="option"]')).find((opt: any) => opt?.hasAttribute('aria-selected'));
 
                if (selectedOption) {
                  const li = selectedOption?.closest('li[role="presentation"]');
                  countryValue = li?.getAttribute('data-value') || '';
                }
              }
 
              console.log('Selected country value:', countryValue);
              this.updateCountryCodeForPhone(countryValue);
              break; // Break the loop if the condition is met
            }
          }
        });
 
        countryListObserver.observe(countryButton, { attributes: true, attributeFilter: ['title', 'aria-label'] });
        (countryButton as any)._countryMutationObserverAdded = true;
      }
 
 
      this.handleCountryFlagVisibility(shadowRoot, countryButton);
 
    }
  };

  /**
   * Handles the visibility of the country flag based on the selected phone number's country code.
   * If the country code is 'IN' or empty, it hides the flag icon and clears the button's title and aria-label.
   * It also sets up event listeners to restore the flag visibility when the user interacts with the menu.
   * @param shadowRoot - The shadow DOM root element where the flag icon is located.
   * @param countryButton - The button element associated with the country selection.
   */
  handleCountryFlagVisibility(shadowRoot: any, countryButton: any) {
    const mobileNumber = this.rootScope.activeCustTab?.otcData?.selectedPhoneNumber;
    const countryCode = this.rootScope.activeCustTab.otcData.selectedCountryCode || 'IN';

    if ((countryCode === 'IN' || countryCode === '') && this.preSelectedCountryCode === false) {
      if (countryButton && !(countryButton as any)._flagClearedOnce) {
        let flagStyleTag = shadowRoot.querySelector('style[data-hide-flag]');
        if (!flagStyleTag) {
          flagStyleTag = document.createElement('style');
          flagStyleTag.setAttribute('data-hide-flag', 'true');
          flagStyleTag.textContent = '.flag-icon { visibility: hidden !important; }';
          shadowRoot.appendChild(flagStyleTag);
        }
        const img = shadowRoot.querySelector('img.flag-icon');
        if (img) {
          const button = img.closest('button');
          if (button) {
            button.title = '';
            button.setAttribute('aria-label', '');
          }
        }
        (countryButton as any)._flagClearedOnce = true;
        const menu = shadowRoot.querySelector('[role="listbox"]');
        if (menu) {
          const restoreFlag = (event: Event) => {
            this.ngZone.run(() => {
              this.countrySelected = true;
            });
            if (flagStyleTag && flagStyleTag.parentNode) {
              flagStyleTag.parentNode.removeChild(flagStyleTag);
            }
            menu.removeEventListener('click', restoreFlag, true);
            menu.removeEventListener('keydown', restoreFlag, true);
          };
          menu.addEventListener('click', restoreFlag, true);
          menu.addEventListener('keydown', restoreFlag, true);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.otcService.openCountryCodeDialog = false;
    this.countryMutationObserver?.disconnect();
  }
}
